import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import type { MerchantReturnDetail as DetailType, TimelineEvent } from "../../domain/merchant";
import {
  getMerchantReturnDetail,
  getReturnTimeline,
  approveReturn,
  rejectReturn,
  markReceived,
  resolveReturn,
} from "../../services/api/merchant";
import { normalizeError } from "../../services/http/errors";

type DialogKind = "approve" | "reject" | "resolve" | "received" | null;

const STATUS_COLOR: Record<string, "warning" | "success" | "info" | "error" | "default"> = {
  REQUESTED: "warning",
  APPROVED: "success",
  IN_TRANSIT: "info",
  RECEIVED: "info",
  RESOLVED: "success",
  REJECTED: "error",
  CANCELLED: "default",
};

const DECISION_COLOR: Record<string, "warning" | "success" | "error" | "default"> = {
  PENDING: "warning",
  APPROVED: "success",
  REJECTED: "error",
};

const ACTION_LABELS: Record<string, string> = {
  return_requested: "Return requested",
  return_approved: "Approved",
  return_rejected: "Rejected",
  return_received: "Marked as received",
  return_resolved: "Resolved",
  return_cancelled: "Cancelled by customer",
  status_change: "Status updated",
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function fmtCurrency(v: number | null | undefined): string {
  if (v == null) return "—";
  return `SAR ${v.toFixed(2)}`;
}

export default function ReturnDetail() {
  const { tenantSlug = "", returnNumber = "" } = useParams();
  const nav = useNavigate();

  const [detail, setDetail] = useState<DetailType | null>(null);
  const [timeline, setTimeline] = useState<TimelineEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success" as "success" | "error", msg: "" });

  // Dialog form fields
  const [notesInternal, setNotesInternal] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [resolveType, setResolveType] = useState("refund");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [d, t] = await Promise.all([
        getMerchantReturnDetail(tenantSlug, returnNumber),
        getReturnTimeline(tenantSlug, returnNumber),
      ]);
      setDetail(d);
      setTimeline(t);
    } catch (e) {
      const err = normalizeError(e);
      setToast({ open: true, severity: "error", msg: err.message });
    } finally {
      setLoading(false);
    }
  }, [tenantSlug, returnNumber]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openDialog = (kind: DialogKind) => {
    setNotesInternal("");
    setStatusReason("");
    setResolveType("refund");
    setDialog(kind);
  };

  const handleAction = async () => {
    if (!detail) return;
    setActionLoading(true);
    try {
      switch (dialog) {
        case "approve":
          await approveReturn(tenantSlug, returnNumber, { notes_internal: notesInternal || undefined });
          break;
        case "reject":
          await rejectReturn(tenantSlug, returnNumber, { status_reason: statusReason, notes_internal: notesInternal || undefined });
          break;
        case "received":
          await markReceived(tenantSlug, returnNumber, { notes_internal: notesInternal || undefined });
          break;
        case "resolve":
          await resolveReturn(tenantSlug, returnNumber, { resolution_type: resolveType, notes_internal: notesInternal || undefined });
          break;
      }
      setDialog(null);
      setToast({ open: true, severity: "success", msg: `Return ${dialog === "received" ? "marked as received" : dialog + "d"} successfully` });
      fetchData();
    } catch (e) {
      const err = normalizeError(e);
      setToast({ open: true, severity: "error", msg: err.message });
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 12 }}>
        <CircularProgress />
      </Stack>
    );
  }

  if (!detail) {
    return (
      <Stack alignItems="center" spacing={2} sx={{ py: 8 }}>
        <Typography color="text.secondary">Return not found</Typography>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={() => nav(`/merchant/${tenantSlug}/inbox`)}>
          Back to Inbox
        </Button>
      </Stack>
    );
  }

  const isTerminal = ["RESOLVED", "REJECTED", "CANCELLED"].includes(detail.status);

  return (
    <>
      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((p) => ({ ...p, open: false }))} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>

      {/* Top bar */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3, flexWrap: "wrap", gap: 1 }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <IconButton onClick={() => nav(`/merchant/${tenantSlug}/inbox`)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5">{detail.returnNumber}</Typography>
          <Chip
            label={detail.status.replace(/_/g, " ")}
            size="small"
            color={STATUS_COLOR[detail.status] || "default"}
            variant="filled"
          />
          {detail.requestedResolution && (
            <Chip label={detail.requestedResolution} size="small" variant="outlined" />
          )}
          {detail.isWarranty && <Chip label="WARRANTY" size="small" color="info" variant="outlined" />}
        </Stack>

        {/* Action buttons */}
        {!isTerminal && (
          <Stack direction="row" spacing={1}>
            {detail.status === "REQUESTED" && (
              <>
                <Button variant="contained" color="success" startIcon={<CheckCircleOutlineIcon />} onClick={() => openDialog("approve")}>
                  Approve
                </Button>
                <Button variant="outlined" color="error" startIcon={<CancelOutlinedIcon />} onClick={() => openDialog("reject")}>
                  Reject
                </Button>
              </>
            )}
            {detail.status === "APPROVED" && (
              <Button variant="contained" startIcon={<LocalShippingOutlinedIcon />} onClick={() => openDialog("received")}>
                Mark Received
              </Button>
            )}
            {detail.status === "RECEIVED" && (
              <Button variant="contained" color="success" startIcon={<VerifiedOutlinedIcon />} onClick={() => openDialog("resolve")}>
                Resolve
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      {/* Info cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <ReceiptLongOutlinedIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">Order</Typography>
              </Stack>
              <Stack spacing={1}>
                <InfoRow label="Order #" value={detail.orderNumber} />
                {detail.orderIdExternal && <InfoRow label="Salla ID" value={detail.orderIdExternal} />}
                <InfoRow label="Requested" value={fmtDate(detail.requestedAt)} />
                {detail.approvedAt && <InfoRow label="Approved" value={fmtDate(detail.approvedAt)} />}
                {detail.receivedAt && <InfoRow label="Received" value={fmtDate(detail.receivedAt)} />}
                {detail.resolvedAt && <InfoRow label="Resolved" value={fmtDate(detail.resolvedAt)} />}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <PersonOutlineIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">Customer</Typography>
              </Stack>
              <Stack spacing={1}>
                <InfoRow label="Contact" value={detail.customerContactMasked || "—"} />
                {detail.notesCustomer && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Customer note</Typography>
                    <Typography variant="body2" sx={{ mt: 0.25, fontStyle: "italic", color: "text.primary" }}>
                      "{detail.notesCustomer}"
                    </Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ height: "100%" }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                <GavelOutlinedIcon fontSize="small" color="action" />
                <Typography variant="subtitle2">Resolution</Typography>
              </Stack>
              <Stack spacing={1}>
                <InfoRow label="Type" value={detail.requestedResolution} />
                <InfoRow label="Value" value={fmtCurrency(detail.totalRequestValue)} />
                <InfoRow label="Items" value={String(detail.totalItemsCount)} />
                {detail.statusReason && <InfoRow label="Reason" value={detail.statusReason} />}
                {detail.notesInternal && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">Internal note</Typography>
                    <Typography variant="body2" sx={{ mt: 0.25 }}>{detail.notesInternal}</Typography>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Items table */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
          <Typography variant="subtitle2" sx={{ px: 2.5, pt: 2.5, pb: 1.5 }}>
            Items ({detail.items.length})
          </Typography>
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Product</TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell align="center">Qty</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="center">Decision</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {detail.items.map((item) => (
                  <TableRow key={item.returnItemId}>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>
                        {item.productName || "Unnamed product"}
                      </Typography>
                      {item.variantName && (
                        <Typography variant="caption" color="text.secondary">{item.variantName}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: 12 }}>{item.sku}</Typography>
                    </TableCell>
                    <TableCell align="center">{item.quantity}</TableCell>
                    <TableCell align="right">{fmtCurrency(item.unitPrice)}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{item.reasonCode.replace(/_/g, " ")}</Typography>
                      {item.reasonNote && (
                        <Typography variant="caption" color="text.secondary">{item.reasonNote}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={item.decision}
                        size="small"
                        color={DECISION_COLOR[item.decision] || "default"}
                        variant="outlined"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </CardContent>
      </Card>

      {/* Timeline */}
      {timeline.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>Activity</Typography>
            <Stack spacing={0} divider={<Divider />}>
              {timeline.map((evt, i) => (
                <Stack key={i} direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ py: 1.5 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {ACTION_LABELS[evt.action] || evt.action.replace(/_/g, " ")}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      by {evt.actorType}{evt.details ? ` — ${summarizeDetails(evt.details)}` : ""}
                    </Typography>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap", ml: 2 }}>
                    {fmtDate(evt.createdAt)}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Approve Dialog */}
      <Dialog open={dialog === "approve"} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve Return</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This will approve {detail.returnNumber} and notify the customer.
          </Typography>
          <TextField
            label="Internal note (optional)"
            multiline
            rows={2}
            fullWidth
            value={notesInternal}
            onChange={(e) => setNotesInternal(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialog(null)} disabled={actionLoading}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleAction} disabled={actionLoading}>
            {actionLoading ? "Approving..." : "Approve"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={dialog === "reject"} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Reject Return</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Let the customer know why this return can't be accepted.
          </Typography>
          <TextField
            label="Rejection reason"
            required
            fullWidth
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Internal note (optional)"
            multiline
            rows={2}
            fullWidth
            value={notesInternal}
            onChange={(e) => setNotesInternal(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialog(null)} disabled={actionLoading}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleAction} disabled={actionLoading || !statusReason.trim()}>
            {actionLoading ? "Rejecting..." : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Received Dialog */}
      <Dialog open={dialog === "received"} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Mark as Received</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Confirm that the returned item(s) have arrived at your warehouse.
          </Typography>
          <TextField
            label="Internal note (optional)"
            multiline
            rows={2}
            fullWidth
            value={notesInternal}
            onChange={(e) => setNotesInternal(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialog(null)} disabled={actionLoading}>Cancel</Button>
          <Button variant="contained" onClick={handleAction} disabled={actionLoading}>
            {actionLoading ? "Updating..." : "Confirm Received"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolve Dialog */}
      <Dialog open={dialog === "resolve"} onClose={() => setDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Resolve Return</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose how to resolve this return for the customer.
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Resolution type</InputLabel>
            <Select value={resolveType} label="Resolution type" onChange={(e) => setResolveType(e.target.value)}>
              <MenuItem value="refund">Refund to original payment</MenuItem>
              <MenuItem value="store_credit">Store credit</MenuItem>
              <MenuItem value="exchange">Exchange for another item</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Internal note (optional)"
            multiline
            rows={2}
            fullWidth
            value={notesInternal}
            onChange={(e) => setNotesInternal(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setDialog(null)} disabled={actionLoading}>Cancel</Button>
          <Button variant="contained" color="success" onClick={handleAction} disabled={actionLoading}>
            {actionLoading ? "Resolving..." : "Resolve"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="body2" fontWeight={500}>{value}</Typography>
    </Stack>
  );
}

function summarizeDetails(details: Record<string, any>): string {
  const parts: string[] = [];
  if (details.status_reason) parts.push(details.status_reason);
  if (details.resolution_type) parts.push(details.resolution_type);
  if (details.notes_internal) parts.push(details.notes_internal);
  return parts.join(", ") || "";
}
